import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { SITE } from "../config";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
  name: "",
  business_name: "",
  email: "",
  phone: "",
  business_type: "",
  looking_for: "",
  budget: "",
  message: "",
};

const BUDGETS = ["Under ₹25,000", "₹25,000 – ₹50,000", "₹50,000 – ₹1,00,000", "₹1,00,000+", "Not sure yet"];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    if (!form.name.trim()) er.name = "Please enter your name";
    if (!EMAIL_RE.test(form.email.trim())) er.email = "Please enter a valid email";
    if (!form.message.trim()) er.message = "Please tell me a little about your project";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Enquiry sent — thank you! I'll be in touch shortly.");
      setForm(INITIAL);
    } catch (err) {
      toast.error("Something went wrong sending your enquiry. Please try again or email me directly.");
    } finally {
      setSending(false);
    }
  };

  const field = (key, label, props = {}) => (
    <div>
      <label htmlFor={`field-${key}`} className="block text-xs uppercase tracking-[0.18em] font-bold text-stone mb-1">
        {label}
      </label>
      <input
        id={`field-${key}`}
        data-testid={`contact-input-${key}`}
        className="input-line"
        value={form[key]}
        onChange={set(key)}
        aria-invalid={!!errors[key]}
        {...props}
      />
      {errors[key] && (
        <p data-testid={`contact-error-${key}`} role="alert" className="mt-1.5 text-xs text-accent font-semibold">
          {errors[key]}
        </p>
      )}
    </div>
  );

  return (
    <section id="contact" data-testid="contact-section" className="max-w-[1400px] mx-auto px-6 md:px-10 py-24 md:py-36">
      <div className="grid md:grid-cols-12 gap-12 md:gap-16">
        <Reveal className="md:col-span-5">
          <div className="flex items-baseline gap-4">
            <span className="font-heading text-xl text-accent">07</span>
            <Eyebrow>Contact</Eyebrow>
          </div>
          <h2 className="mt-6 font-heading font-medium tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl text-ink">
            Let's build something.
          </h2>
          <p className="mt-6 text-base md:text-lg text-stone leading-relaxed max-w-md">
            Share a few details about your business and what you're looking for.
            I usually reply within one working day.
          </p>

          <dl className="mt-10 space-y-5 border-t border-line pt-8">
            {[
              ["Email", SITE.email, `mailto:${SITE.email}`, "contact-email"],
              ["WhatsApp", SITE.whatsappDisplay, SITE.whatsappUrl, "contact-whatsapp"],
            ].map(([label, display, href, tid]) => (
              <div key={label} className="flex items-baseline justify-between gap-4">
                <dt className="text-xs uppercase tracking-[0.18em] font-bold text-stone">{label}</dt>
                <dd>
                  <a
                    href={href}
                    target={href.startsWith("mailto") ? undefined : "_blank"}
                    rel="noopener noreferrer"
                    data-testid={tid}
                    className="text-base font-semibold text-ink hover:text-accent transition-colors duration-300"
                  >
                    {display}
                  </a>
                </dd>
              </div>
            ))}
          </dl>
        </Reveal>

        <Reveal delay={0.12} className="md:col-span-7">
          <form data-testid="contact-form" onSubmit={submit} noValidate className="space-y-8 border border-line p-8 md:p-12 bg-surface/50">
            <div className="grid sm:grid-cols-2 gap-8">
              {field("name", "Name *", { autoComplete: "name" })}
              {field("business_name", "Business Name", { autoComplete: "organization" })}
            </div>
            <div className="grid sm:grid-cols-2 gap-8">
              {field("email", "Email *", { type: "email", autoComplete: "email" })}
              {field("phone", "Phone / WhatsApp", { type: "tel", autoComplete: "tel" })}
            </div>
            {field("business_type", "What does your business do?")}
            {field("looking_for", "What are you looking for?")}
            <div>
              <label htmlFor="field-budget" className="block text-xs uppercase tracking-[0.18em] font-bold text-stone mb-1">
                Estimated Budget
              </label>
              <select
                id="field-budget"
                data-testid="contact-input-budget"
                className="input-line cursor-pointer"
                value={form.budget}
                onChange={set("budget")}
              >
                <option value="">Select a range</option>
                {BUDGETS.map((b) => (
                  <option key={b} value={b}>{b}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="field-message" className="block text-xs uppercase tracking-[0.18em] font-bold text-stone mb-1">
                Message *
              </label>
              <textarea
                id="field-message"
                data-testid="contact-input-message"
                rows={4}
                className="input-line resize-y"
                value={form.message}
                onChange={set("message")}
                aria-invalid={!!errors.message}
              />
              {errors.message && (
                <p data-testid="contact-error-message" role="alert" className="mt-1.5 text-xs text-accent font-semibold">
                  {errors.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              data-testid="contact-submit-button"
              disabled={sending}
              className="group inline-flex items-center gap-2 bg-ink text-ivory font-semibold tracking-wide px-8 py-4 hover:bg-accent transition-colors duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {sending ? "Sending…" : "Send Enquiry"}
              <ArrowUpRight size={17} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
